<template>
  <div>
    <v-row justify="space-between" v-if="search">
      <v-col cols="auto">
        <SearchText
          v-if="$store.getters.roles === 'super-admin'"
          :text="'Search by name'"
          :value="'name'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
        ></SearchText>
      </v-col>
      <v-col v-if="$store.getters.roles === 'super-admin'" class="d-flex align-center">
        <span class="mr-2 primary200--text font-weight-bold">Enabled Institutions</span>
        <v-switch v-model="institutionsEnabled" color="success"></v-switch>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <div class="d-flex flex-wrap">
          <v-btn
            v-if="$store.getters.roles === 'super-admin'"
            @click="downloadHostingInstitutions"
            outlined
            class="black--text font-weight-bold mb-2"
            rounded
            large
            :disabled="downloadLoading"
          >
            <template v-if="downloadLoading">
              <v-progress-circular
                :rotate="360"
                indeterminate
                size="20"
                width="3"
                color="black"
                class="mr-2"
              />
            </template>
            <template v-else>
              <v-icon color="black" small class="mr-2">
                mdi-download
              </v-icon>
              Download
            </template>
          </v-btn>
          <v-btn
            v-if="$store.getters.roles === 'super-admin'"
            :to="{ name: 'AddHostingInstitution' }"
            outlined
            class="white--text font-weight-bold mb-2 black ml-2"
            rounded
            large
          >
            Add Hosting Institution
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      class="mt-5"
      :items="items"
      :options.sync="options"
      :server-items-length="totalItems"
      id="equipment-values"
      v-model="hostingInstitutions"
      :no-data-text="noDataText"
    >
      <template v-slot:item.actions="{ item }">
        <v-layout>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                :to="{
                  name: 'EditHostingInstitution',
                  params: {
                    id: item.id,
                  },
                  query: {
                    fromTable: true,
                    ...getParams(),
                  },
                }"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-pencil-outline
                </v-icon>
              </v-btn>
            </template>
            {{ $t('edit') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                :to="{
                  name: 'OverviewHostingInstitution',
                  params: { id: item.id },
                  query: {
                    fromTable: true,
                    ...getParams(),
                  },
                }"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-eye
                </v-icon>
              </v-btn>
            </template>
            {{ $t('overview') }}
          </v-tooltip>
          <v-tooltip bottom v-if="!item.disabled">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="(itemToBeDisabled = item), (disabledDialog = true)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-cancel
                </v-icon>
              </v-btn>
            </template>
            {{ $t('disable.message') }}
          </v-tooltip>
          <v-tooltip bottom v-if="item.disabled && $store.getters.roles === 'super-admin'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="(itemToBeDeleted = item), (deleteDialog = true)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
            {{ $t('delete.message') }}
          </v-tooltip>
          <v-tooltip bottom v-if="item.disabled">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="(itemToBeDisabled = item), (disabledDialog = true)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-check
                </v-icon>
              </v-btn>
            </template>
            {{ $t('enable.message') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="$store.getters.roles === 'super-admin'"
                @click="onEditMembers(item)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-account-multiple
                </v-icon>
              </v-btn>
              <v-btn
                v-if="$store.getters.roles === 'provider'"
                @click="onEditMembersAsProvider(item)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-account-multiple
                </v-icon>
              </v-btn>
            </template>
            {{ $t('members') }}
          </v-tooltip>
        </v-layout>
      </template>
      <template v-slot:item.logo="{ item }">
        <div class="table-logo">
          <v-img
            class="my-2"
            style="border-radius: 8px;"
            lazy-src="@/assets/img/blur.jpg"
            height="65"
            width="65"
            :src="item.logoUrl"
            contain
          ></v-img>
        </div>
      </template>
      <template v-slot:item.administrator="{ item }">
        <div
          v-if="
            item.administratorContact &&
              item.administratorContact.lastname &&
              item.administratorContact.firstname
          "
          class="font-weight-bold"
        >
          {{ item.administratorContact.lastname }} {{ item.administratorContact.firstname }}
        </div>
        <div v-else>
          No Administrator yet
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip v-if="item.disabled" outlined color="danger" class="font-weight-bold">
          <v-icon left>
            mdi-exclamation
          </v-icon>
          Disabled
        </v-chip>
        <v-chip v-else outlined color="success" class="font-weight-bold">
          <v-icon left>
            mdi-check
          </v-icon>
          Enabled
        </v-chip>
      </template>
    </v-data-table>

    <!-- Dialog for Delete HI -->
    <v-dialog v-if="itemToBeDeleted !== null" v-model="deleteDialog" max-width="1000px" persistent>
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="px-0 py-0">
          <v-row class="mx-0 px-2" align="center">
            <v-col cols="11" class="justify-center d-flex">
              <img
                alt="Logo"
                width="180"
                class="rounded-0 text-center"
                :src="require('@/assets/svg/logo.svg')"
              />
            </v-col>
            <v-col cols="1" class="pl-0">
              <v-btn fab small text @click="deleteDialog = false">
                <v-icon color="neutral500">
                  mdi-close
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="custom-divider mt-10"></v-divider>

        <v-card-text class="justify-center d-flex mt-2">
          <v-icon size="150" color="red">$vuetify.icons.deleteCircle</v-icon>
        </v-card-text>
        <v-card-text class="text-center">
          <h3>Delete your Hosting Institution?</h3>
        </v-card-text>
        <v-card-text>
          <p class="text-center pl-16 pr-16">
            Are you sure you want to permanently delete your Hosting Institution? <br />
            This action
            <b class="red--text">cannot be undone.</b>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                @click="deleteDialog = false"
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="neutral"
                outlined
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                @click="deleteHosting"
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="red"
                >{{ $t('confirm') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Disable/Enable -->
    <v-dialog v-if="itemToBeDisabled !== null" v-model="disabledDialog" max-width="500">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title>{{
          itemToBeDisabled.disabled ? $t('enable.message') : $t('disable.message')
        }}</v-card-title>
        <v-card-text>
          {{ itemToBeDisabled.disabled ? $t('enable.confirm') : $t('disable.confirm') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            rounded
            large
            class="neutral800--text font-weight-bold"
            color="neutral"
            outlined
            @click="disabledDialog = false"
            >{{ $t('cancel') }}
          </v-btn>
          <v-btn
            v-if="itemToBeDisabled.disabled"
            depressed
            rounded
            large
            class="white--text font-weight-bold"
            color="danger"
            @click="onEnableItem"
            :disabled="disableDisabled"
            >{{ $t('enable.message') }}
          </v-btn>
          <v-btn
            v-else
            depressed
            rounded
            large
            class="white--text font-weight-bold"
            color="danger"
            @click="onDisabledItem"
            :disabled="disableDisabled"
            >{{ $t('disable.message') }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import SearchText from '@/components/shared/search/SearchText';
import util from '@/mixins/util.js';

export default {
  name: 'TableHostingInstitutions',
  components: { SearchText },
  mixins: [util],
  props: {
    urlApi: String,
    search: Object,
    searchAttributes: Array,
  },
  data: () => ({
    headers: [
      { text: '', value: 'actions', width: '1px' },
      {
        text: 'Logo',
        value: 'logo',
        width: '10px',
        sortable: false,
      },
      { text: 'Full name', value: 'name', sortable: true },
      { text: 'Administrator', value: 'administrator', sortable: true },
      { text: 'Status', value: 'status', sortable: true },
    ],
    itemToBeDisabled: null,
    disabledDialog: false,
    deleteDialog: false,
    itemToBeDeleted: null,
    disableDisabled: false,
    items: [],
    totalItems: 0,
    options: {},
    firstLoad: true,
    hostingInstitutions: [],
    downloadLoading: false,
    institutionsEnabled: true,
  }),
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        }
      },
      deep: true,
    },
    institutionsEnabled() {
      this.changeOptions();
    },
  },
  mounted() {
    console.log('[Table] Mounted');
    if (this.firstLoad) {
      /* Populate table from route */
      this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
      this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
      this.options.sortBy = [];
      this.options.sortDesc = [];
      if (this.$route.query.institutionsEnabled !== undefined) {
        this.institutionsEnabled = this.$route.query.institutionsEnabled === 'false' ? false : true;
      }
      if (this.$route.query.institutionsEnabled == false) {
        this.institutionsEnabled = false;
      }
      if (this.$route.query.sort) {
        const sortList = this.$route.query.sort.split(',');
        for (let i = 0; i < sortList.length; i += 1) {
          this.options.sortBy.push(sortList[i].split(' ')[0]);
          if (sortList[i].split(' ')[1] === 'asc') {
            this.options.sortDesc.push(false);
          } else {
            this.options.sortDesc.push(true);
          }
        }
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.$route.query[attr]) {
            this.$set(this.search, attr, this.$route.query[attr]);
          }
        }
      }
    }
    this.getDataFromApi().then(data => {
      this.items = data.items;
      this.totalItems = data.total;
      this.firstLoad = false;
    });
  },
  computed: {
    noDataText() {
      return this.$store.getters.roles === 'provider'
        ? 'You are not assigned as an administrator member for any Hosting Institution'
        : 'No data available';
    },
  },
  methods: {
    changeOptions() {
      this.$router.push({
        path: this.$router.path,
        query: this.getParams(),
      });
      let currentPathName = this.$router.currentRoute.name;
      localStorage[currentPathName] = this.$router.currentRoute.fullPath;
    },
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    getDataFromApi() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);

      this.role = localStorage.getItem('roles') || '';
      console.log('role:', this.role);

      if (this.role === 'provider') {
        return new Promise(resolve => {
          this.axios
            .get(`${this.urlApi}?providerId=${sessionStorage.getItem('providerId')}`, {
              params: this.getParams(),
            })
            .then(res => {
              let items = [];
              let total = 0;
              if (res.status === 200) {
                items = res.data.content;
                total = res.data.total;
                console.log('Get Institutions', items);
              }
              setTimeout(() => {
                this.loading = false;
                resolve({
                  items,
                  total,
                });
              });
            })
            .finally(() => {
              EventBus.$emit('waiting', false);
            });
        });
      }

      if (this.role === 'super-admin') {
        return new Promise(resolve => {
          this.axios
            .get(`api/v1/hosting-institutions`, {
              params: this.getParams(),
            })
            .then(res => {
              let items = [];
              let total = 0;
              if (res.status === 200) {
                items = res.data.content;
                total = res.data.total;
                console.log('Get Institutions', items);
              }
              setTimeout(() => {
                this.loading = false;
                resolve({
                  items,
                  total,
                });
              });
            })
            .finally(() => {
              EventBus.$emit('waiting', false);
            });
        });
      }
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const query = {
        index: page - 1,
        size: itemsPerPage,
        institutionsEnabled: this.institutionsEnabled,
      };
      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }
        sort += `${sortBy[i]} ${dir}`;
      }
      if (sort !== '') {
        query.sort = sort;
      }

      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.search[attr]) {
            query[attr] = this.search[attr];
          }
        }
      }

      return query;
    },
    downloadHostingInstitutions() {
      this.downloadLoading = true;
      this.axios
        .get('api/v1/hosting-institutions/admin/download', {
          responseType: 'blob',
        })
        .then(response => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = 'Hosting Institutions.xlsx';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch) {
              fileName = fileNameMatch[1];
            }
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);

          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        })
        .finally(() => {
          this.downloadLoading = false;
        });
    },
    onDisabledItem() {
      this.disableDisabled = true;
      let value = this.itemToBeDisabled;
      console.log('Click Disabled ' + value.id);
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/hosting-institutions/disable/' + value.id)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('disable.success'),
          });
          this.disabledDialog = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
          this.disableDisabled = false;
        })
        .catch(error => {
          console.log(error);
          this.disableDisabled = false;
        })
        .finally(EventBus.$emit('waiting', false));
    },
    deleteHosting() {
      let value = this.itemToBeDeleted;
      console.log('Click Delete' + value.id);
      EventBus.$emit('waiting', true);
      this.axios
        .delete('api/v1/hosting-institutions/delete/' + value.id)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('delete.success'),
          });
          this.deleteDialog = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        })
        .catch(error => {
          console.log(error);
          this.deleteDialog = false;

          const message = error?.response?.data?.description;

          EventBus.$emit('snackbar', {
            text: message,
            color: 'warning',
          });
        })
        .finally(EventBus.$emit('waiting', false));
    },
    onEnableItem() {
      this.disableDisabled = true;
      let value = this.itemToBeDisabled;
      console.log('Click Disabled ' + value.id);
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/hosting-institutions/enable/' + value.id)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('enable.success'),
          });
          this.disabledDialog = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
          this.disableDisabled = false;
        })
        .catch(error => {
          console.log(error);
          this.disableDisabled = false;
        })
        .finally(EventBus.$emit('waiting', false));
    },
    onEditMembers(value) {
      console.log('Click Members for HI ' + value.id);
      this.$router.push({
        name: 'EditHostingInstitutionsMembers',
        params: { id: value.id },
        query: {
          hosting: true,
          fromTable: true,
          ...this.getParams(),
        },
      });
    },
    onEditMembersAsProvider(value) {
      console.log('Click Members for HI ' + value.id);
      this.$router.push({
        name: 'AdminMembersOfInstitutions',
        params: { id: value.id },
        query: {
          adminMember: true,
          fromTable: true,
          ...this.getParams(),
        },
      });
    },
  },
};
</script>
